import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { CellHookData } from 'jspdf-autotable';
import { bandwidthExportDateFileSuffix } from '../bandwidth/bandwidthExportFileNaming';
import type { KpiHeatmapCiApiData, KpiHeatmapCustomerApiData } from './kpiDashboardApi';
import type {
  KpiData,
  KpiLastUpdatedRow,
  KpiOperationalAgeingRow,
  KpiResilienceData,
} from './types';

export type KpiPdfExport = { blob: Blob; fileName: string };

const PDF_HEADER_BG: [number, number, number] = [68, 114, 196];
const PDF_HEADER_TEXT: [number, number, number] = [255, 255, 255];
const PDF_TITLE: [number, number, number] = [30, 41, 59];
const PDF_BODY_TEXT: [number, number, number] = [15, 23, 42];
const PDF_MUTED: [number, number, number] = [100, 116, 139];
const PDF_BORDER: [number, number, number] = [199, 210, 254];
const PDF_ZEBRA_A: [number, number, number] = [255, 255, 255];
const PDF_ZEBRA_B: [number, number, number] = [248, 250, 252];
const PDF_TOTAL_BG: [number, number, number] = [226, 232, 240];

function kpiPdfFilename(base: string, dateFrom: string, dateTo: string): string {
  return `${base}_${bandwidthExportDateFileSuffix(dateFrom, dateTo)}.pdf`;
}

function pdfAsciiSafeText(s: string): string {
  return s
    .replace(/\u2192/g, ' to ')
    .replace(/\u2014/g, '-')
    .replace(/\u2013/g, '-')
    .replace(/\u00A0/g, ' ');
}

function toYmdDisplay(isoOrDate: string): string {
  const s = (isoOrDate || '').trim();
  const d = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const cleaned = s.replace(/[^\d-]/g, '').slice(0, 10);
  return cleaned || 'n/a';
}

function periodSubtitle(dateFrom: string, dateTo: string): string {
  return `Duration: ${toYmdDisplay(dateFrom)} to ${toYmdDisplay(dateTo)}`;
}

function drawKpiPdfTitleBlock(
  doc: jsPDF,
  title: string,
  dateFrom: string,
  dateTo: string,
  opts?: { unitNote?: string; titleY?: number }
): number {
  const margin = 14;
  const titleY = opts?.titleY ?? 12;
  doc.setTextColor(...PDF_TITLE);
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.text(pdfAsciiSafeText(title), margin, titleY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...PDF_MUTED);
  const periodY = titleY + 5.5;
  doc.text(periodSubtitle(dateFrom, dateTo), margin, periodY);
  let lastTextY = periodY;
  if (opts?.unitNote) {
    doc.setFontSize(8);
    lastTextY = periodY + 4;
    doc.text(pdfAsciiSafeText(opts.unitNote), margin, lastTextY);
  }
  doc.setTextColor(...PDF_BODY_TEXT);
  return lastTextY + 6;
}

function finalizeKpiPdfFooters(doc: jsPDF): void {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  const generated = new Date().toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_MUTED);
    doc.text(pdfAsciiSafeText(`Generated ${generated}`), 14, pageH - 8);
    doc.text(`Page ${i} of ${total}`, pageW - 14, pageH - 8, { align: 'right' });
    doc.setDrawColor(...PDF_BORDER);
    doc.setLineWidth(0.25);
    doc.line(14, pageH - 11, pageW - 14, pageH - 11);
    doc.setTextColor(...PDF_BODY_TEXT);
  }
}

const defaultHeadStyles = {
  fillColor: PDF_HEADER_BG,
  textColor: PDF_HEADER_TEXT,
  fontStyle: 'bold' as const,
  fontSize: 8,
  cellPadding: 2,
};

const defaultTableStyles = {
  fontSize: 8,
  cellPadding: 2,
  textColor: PDF_BODY_TEXT,
  lineColor: PDF_BORDER,
  lineWidth: 0.12,
};

const pdfTablePlainTheme = { theme: 'plain' as const };

function styleBodyCellSimpleTotal(data: CellHookData, boldRows: Set<number>): void {
  if (data.section !== 'body') return;
  const idx = data.row.index;
  if (boldRows.has(idx)) {
    data.cell.styles.fillColor = PDF_TOTAL_BG;
    data.cell.styles.fontStyle = 'bold';
    return;
  }
  data.cell.styles.fillColor = idx % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
}

function docNextY(doc: jsPDF, padMm = 8): number {
  const last = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY;
  return (last ?? 14) + padMm;
}

/** Generic customer / CI / matrix export (same layout as Bandwidth heatmap matrix PDF). */
export function exportKpiHeatmapMatrixPdf(params: {
  documentTitle: string;
  filePrefix: string;
  rowLabelHeader: string;
  rowLabels: string[];
  columnLabels: string[];
  cells: number[][];
  dateFrom: string;
  dateTo: string;
  unitNote?: string;
}): KpiPdfExport | null {
  const { documentTitle, filePrefix, rowLabelHeader, rowLabels, columnLabels, cells, unitNote, dateFrom, dateTo } =
    params;
  if (rowLabels.length === 0 || columnLabels.length === 0) return null;

  const head = [[rowLabelHeader, ...columnLabels]];
  const body: (string | number)[][] = [];
  const colSums = columnLabels.map((_, ci) => rowLabels.reduce((s, _, ri) => s + (cells[ri]?.[ci] ?? 0), 0));

  for (let ri = 0; ri < rowLabels.length; ri++) {
    const row = cells[ri] ?? [];
    body.push([
      rowLabels[ri],
      ...columnLabels.map((_, ci) => {
        const v = row[ci] ?? 0;
        return v > 0 ? v : '';
      }),
    ]);
  }
  const lastIdx = body.length;
  body.push(['Total', ...colSums]);
  const boldRows = new Set<number>([lastIdx]);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const startY = drawKpiPdfTitleBlock(doc, documentTitle, dateFrom, dateTo, { unitNote });
  const fs = columnLabels.length > 16 ? 5.5 : columnLabels.length > 10 ? 6.5 : 7.5;
  const pad = columnLabels.length > 14 ? 0.7 : 1.2;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: {
      ...defaultTableStyles,
      fontSize: fs,
      cellPadding: pad,
    },
    headStyles: { ...defaultHeadStyles, fontSize: fs, cellPadding: pad },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename(filePrefix, dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

function sortedHeatmapRows(heatmap: KpiHeatmapCustomerApiData['heatmap']) {
  const copy = [...heatmap.rows];
  copy.sort((a, b) => a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' }));
  return copy;
}

export function exportKpiHeatmapCustomerViewPdf(
  data: KpiHeatmapCustomerApiData,
  dateFrom: string,
  dateTo: string,
  titleSuffix: string
): KpiPdfExport | null {
  const { heatmap } = data;
  const rows = sortedHeatmapRows(heatmap);
  return exportKpiHeatmapMatrixPdf({
    documentTitle: `KPI Dashboard - ${titleSuffix}`,
    filePrefix: 'kpi_heatmap_customer',
    rowLabelHeader: 'Customer',
    rowLabels: rows.map((r) => r.customerName),
    columnLabels: heatmap.weekLabels,
    cells: rows.map((r) => r.counts),
    dateFrom,
    dateTo,
    unitNote: 'Cell values are open backlog counts by week.',
  });
}

export function exportKpiHeatmapCiViewPdf(
  heatmap: KpiHeatmapCiApiData['heatmap'],
  dateFrom: string,
  dateTo: string
): KpiPdfExport | null {
  const rows = [...heatmap.rows].sort((a, b) => a.ciLabel.localeCompare(b.ciLabel, undefined, { sensitivity: 'base' }));
  return exportKpiHeatmapMatrixPdf({
    documentTitle: 'KPI Dashboard - Heatmap (CI)',
    filePrefix: 'kpi_heatmap_ci',
    rowLabelHeader: 'Configuration item',
    rowLabels: rows.map((r) => r.ciLabel),
    columnLabels: heatmap.weekLabels,
    cells: rows.map((r) => r.counts),
    dateFrom,
    dateTo,
    unitNote: 'Cell values are ticket counts by week.',
  });
}

export function exportKpiDashboardMainPdf(data: KpiData, dateFrom: string, dateTo: string): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Main', dateFrom, dateTo, { titleY: 12 });

  const metricsHead = [['Metric', 'Value']];
  const metricsBody: (string | number)[][] = [
    ['Tickets total', data.ticketsTotal],
    ['Incidents volume', data.incidentsVolume],
    ['Service requests volume', data.requestsVolume],
    ['Cases volume', data.casesVolume],
    ['Problems volume', data.problemsVolume],
    ['Changes volume', data.changesVolume],
    ['MTTR (minutes)', data.mttrMinutes],
    ['MTTR incidents (minutes)', data.incidentsMttrMinutes],
    ['MTTR requests (minutes)', data.requestsMttrMinutes],
    ['Change failure rate', data.changeFailureRate],
    ['Changes success rate', data.changesSuccessRate],
    ['Changes MTTR (minutes)', data.changesMttrMinutes],
    ['Tickets avg open days', data.ticketsAvgOpenDays],
    ['Incidents avg open days', data.incidentsAvgOpenDays],
    ['Requests avg open days', data.requestsAvgOpenDays],
    ['Cases avg open days', data.casesAvgOpenDays],
    ['Case response SLA met rate', data.caseResponseSlaMetRate],
    ['Incident ongoing SLA met rate', data.incidentOngoingSlaMetRate],
    ['Request ongoing SLA met rate', data.requestOngoingSlaMetRate],
    ['Incident resolution SLA met rate', data.incidentResolutionSlaMetRate],
    ['Request resolution SLA met rate', data.requestResolutionSlaMetRate],
  ];

  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: metricsHead,
    body: metricsBody,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 75 } },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  y = docNextY(doc, 10);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Backlog age (open)', 14, y);
  y += 5;
  const backlogEntries = Object.entries(data.backlogAge);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Bucket', 'Count']],
    body: backlogEntries.map(([k, v]) => [k, v]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  y = docNextY(doc, 10);
  doc.setFontSize(10);
  doc.text('Priority counts', 14, y);
  y += 5;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Priority', 'Count']],
    body: Object.entries(data.priorityCounts).map(([k, v]) => [k, v]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  if (data.operationalAgeingByGroup.length > 0) {
    y = docNextY(doc, 10);
    doc.setFont('helvetica', 'bold');
    doc.text('Open backlog ageing (2+ days)', 14, y);
    y += 5;
    autoTable(doc, {
      ...pdfTablePlainTheme,
      startY: y,
      head: [
        [
          'Type',
          'Assignment group',
          '2-5 d',
          '6-10 d',
          '11-15 d',
          '16-30 d',
          '>30 d',
          'Total',
        ],
      ],
      body: data.operationalAgeingByGroup.map((r: KpiOperationalAgeingRow) => [
        r.ticketType,
        pdfAsciiSafeText(r.assignmentGroupName),
        r.days2to5,
        r.days6to10,
        r.days11to15,
        r.days16to30,
        r.daysOver30,
        r.rowTotal,
      ]),
      styles: { ...defaultTableStyles, fontSize: 7 },
      headStyles: { ...defaultHeadStyles, fontSize: 7 },
      didParseCell: (data) => {
        if (data.section === 'body' && typeof data.row.index === 'number') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
        }
      },
    });
  }

  if (data.lastUpdatedByGroup.length > 0) {
    y = docNextY(doc, 10);
    doc.setFont('helvetica', 'bold');
    doc.text('Open backlog last updated', 14, y);
    y += 5;
    autoTable(doc, {
      ...pdfTablePlainTheme,
      startY: y,
      head: [['Type', 'Assignment group', '0-24 h', '>24 h', 'Total']],
      body: data.lastUpdatedByGroup.map((r: KpiLastUpdatedRow) => [
        r.ticketType,
        pdfAsciiSafeText(r.assignmentGroupName),
        r.within24h,
        r.over24h,
        r.rowTotal,
      ]),
      styles: defaultTableStyles,
      headStyles: defaultHeadStyles,
      didParseCell: (data) => {
        if (data.section === 'body' && typeof data.row.index === 'number') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
        }
      },
    });
  }

  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_main', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportKpiResiliencePdf(resilience: KpiResilienceData, dateFrom: string, dateTo: string): KpiPdfExport {
  const h = resilience.mttrHeatmap;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Resilience', dateFrom, dateTo, {
    unitNote: 'MTTR heatmap cells show mean minutes to resolve (blank when no data).',
  });

  if (h.weekLabels.length && h.weekdayLabels.length) {
    const head = [['Week', ...h.weekdayLabels]];
    const body: (string | number)[][] = h.weekLabels.map((wl, ri) => {
      const row = h.mttrByWeekAndWeekday[ri] ?? [];
      return [
        wl,
        ...h.weekdayLabels.map((_, ci) => {
          const v = row[ci];
          return v != null && Number.isFinite(v) ? Number(v).toFixed(1) : '-';
        }),
      ];
    });
    autoTable(doc, {
      ...pdfTablePlainTheme,
      startY: y,
      head,
      body,
      styles: { ...defaultTableStyles, fontSize: 7 },
      headStyles: { ...defaultHeadStyles, fontSize: 7 },
      didParseCell: (data) => {
        if (data.section === 'body' && typeof data.row.index === 'number') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
        }
      },
    });
    y = docNextY(doc, 10);
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('MTTR by priority', 14, y);
  y += 5;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Priority', 'MTTR (min)', 'Count']],
    body: resilience.mttrByPriority.map((r) => [
      r.priorityLabel,
      r.mttrMinutes != null && Number.isFinite(r.mttrMinutes) ? Number(r.mttrMinutes).toFixed(1) : '-',
      r.count,
    ]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  y = docNextY(doc, 10);
  doc.text('MTTR trend by week', 14, y);
  y += 5;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Week', 'Inc MTTR', 'Req MTTR', 'Inc n', 'Req n']],
    body: resilience.mttrTrendByWeek.map((r) => [
      r.weekLabel,
      r.incidentMttrMinutes != null ? Number(r.incidentMttrMinutes).toFixed(1) : '-',
      r.requestMttrMinutes != null ? Number(r.requestMttrMinutes).toFixed(1) : '-',
      r.incidentCount,
      r.requestCount,
    ]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  y = docNextY(doc, 10);
  doc.text('MTTR by assignment group', 14, y);
  y += 5;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Group', 'MTTR (min)', 'Count']],
    body: resilience.mttrByAssignmentGroup.map((r) => [
      pdfAsciiSafeText(r.groupName),
      r.mttrMinutes != null && Number.isFinite(r.mttrMinutes) ? Number(r.mttrMinutes).toFixed(1) : '-',
      r.count,
    ]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_resilience', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

function appendHeatmapMatrixToDoc(
  doc: jsPDF,
  startY: number,
  rowLabelHeader: string,
  rowLabels: string[],
  columnLabels: string[],
  cells: number[][]
): number {
  if (rowLabels.length === 0 || columnLabels.length === 0) return startY;
  const head = [[rowLabelHeader, ...columnLabels]];
  const body: (string | number)[][] = [];
  const colSums = columnLabels.map((_, ci) => rowLabels.reduce((s, _, ri) => s + (cells[ri]?.[ci] ?? 0), 0));
  for (let ri = 0; ri < rowLabels.length; ri++) {
    const row = cells[ri] ?? [];
    body.push([
      rowLabels[ri],
      ...columnLabels.map((_, ci) => {
        const v = row[ci] ?? 0;
        return v > 0 ? v : '';
      }),
    ]);
  }
  const lastIdx = body.length;
  body.push(['Total', ...colSums]);
  const boldRows = new Set<number>([lastIdx]);
  const fs = columnLabels.length > 16 ? 5.5 : columnLabels.length > 10 ? 6.5 : 7.5;
  const pad = columnLabels.length > 14 ? 0.7 : 1.2;
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY,
    head,
    body,
    styles: {
      ...defaultTableStyles,
      fontSize: fs,
      cellPadding: pad,
    },
    headStyles: { ...defaultHeadStyles, fontSize: fs, cellPadding: pad },
    didParseCell: (data) => styleBodyCellSimpleTotal(data, boldRows),
  });
  return docNextY(doc, 8);
}

export function exportKpiIncidentTabPdf(
  data: KpiData,
  heatmap: KpiHeatmapCustomerApiData,
  dateFrom: string,
  dateTo: string
): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Incident', dateFrom, dateTo, {
    unitNote: 'Heatmap: open backlog counts by customer and week.',
  });
  const rows = sortedHeatmapRows(heatmap.heatmap);
  y = appendHeatmapMatrixToDoc(
    doc,
    y,
    'Customer',
    rows.map((r) => r.customerName),
    heatmap.heatmap.weekLabels,
    rows.map((r) => r.counts)
  );
  doc.addPage();
  y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Incident (continued)', dateFrom, dateTo, { titleY: 12 });
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['KPI', 'Value']],
    body: [
      ['Incidents volume', data.incidentsVolume],
      ['Incidents avg open days', data.incidentsAvgOpenDays],
      ['MTTR (minutes)', data.mttrMinutes],
    ],
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Assignment group', 'Count', 'SLA breach %']],
    body: data.incidentsByAssignmentGroup.map((r) => [pdfAsciiSafeText(r.groupName), r.count, r.slaBreachPct]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_incident', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportKpiServiceRequestTabPdf(
  data: KpiData,
  heatmap: KpiHeatmapCustomerApiData,
  dateFrom: string,
  dateTo: string
): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Service Request', dateFrom, dateTo, {
    unitNote: 'Heatmap: open backlog counts by customer and week.',
  });
  const rows = sortedHeatmapRows(heatmap.heatmap);
  y = appendHeatmapMatrixToDoc(
    doc,
    y,
    'Customer',
    rows.map((r) => r.customerName),
    heatmap.heatmap.weekLabels,
    rows.map((r) => r.counts)
  );
  doc.addPage();
  y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Service Request (continued)', dateFrom, dateTo, { titleY: 12 });
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['KPI', 'Value']],
    body: [
      ['Requests volume', data.requestsVolume],
      ['Requests avg open days', data.requestsAvgOpenDays],
      ['MTTR (minutes)', data.mttrMinutes],
    ],
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Assignment group', 'Count', 'SLA breach %']],
    body: data.requestsByAssignmentGroup.map((r) => [pdfAsciiSafeText(r.groupName), r.count, r.slaBreachPct]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Customer', 'Count']],
    body: data.requestsByCustomerTreemap.map((r) => [pdfAsciiSafeText(r.customerName), r.count]),
    styles: { ...defaultTableStyles, fontSize: 7 },
    headStyles: { ...defaultHeadStyles, fontSize: 7 },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_service_request', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportKpiCaseTabPdf(data: KpiData, dateFrom: string, dateTo: string): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Case', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['KPI', 'Value']],
    body: [
      ['Cases volume', data.casesVolume],
      ['Cases avg open days', data.casesAvgOpenDays],
    ],
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Week', 'Count']],
    body: data.casesVolumeByWeek.map((r) => [r.weekLabel, r.count]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Customer', 'Count']],
    body: data.casesByCustomerTreemap.map((r) => [pdfAsciiSafeText(r.customerName), r.count]),
    styles: { ...defaultTableStyles, fontSize: 7 },
    headStyles: { ...defaultHeadStyles, fontSize: 7 },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Assignment group', 'Count', 'SLA breach %']],
    body: data.casesByAssignmentGroup.map((r) => [pdfAsciiSafeText(r.groupName), r.count, r.slaBreachPct]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_case', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportKpiChangeRequestTabPdf(data: KpiData, dateFrom: string, dateTo: string): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Change Request', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['KPI', 'Value']],
    body: [
      ['Changes volume', data.changesVolume],
      ['Change failure rate', data.changeFailureRate],
      ['Changes success rate', data.changesSuccessRate],
      ['Changes MTTR (minutes)', data.changesMttrMinutes],
    ],
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Assignment group', 'Count', 'Success %']],
    body: data.changesByAssignmentGroup.map((r) => [pdfAsciiSafeText(r.groupName), r.count, r.successPct]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Close code', 'Count']],
    body: data.changesCloseCodeSlices.map((r) => [pdfAsciiSafeText(r.label), r.count]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  y = docNextY(doc, 8);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['State', 'Count']],
    body: data.changesByState.map((r) => [pdfAsciiSafeText(r.stateLabel), r.count]),
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_change_request', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

function truncatePdf(s: string, max: number): string {
  const t = pdfAsciiSafeText(s || '');
  return t.length <= max ? t : `${t.slice(0, max - 3)}...`;
}

export function exportKpiProblemsTabPdf(data: KpiData, dateFrom: string, dateTo: string): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Problems', dateFrom, dateTo);
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['KPI', 'Value']],
    body: [
      ['Problems volume', data.problemsVolume],
      ['Problems with knowledge %', data.problemsWithKnowledgePct],
    ],
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
  });
  y = docNextY(doc, 8);

  const st = data.problemsStateCustomerHeatmap;
  if (st.stateLabels.length && st.rows.length) {
    const head = [['Company', ...st.stateLabels]];
    const body = st.rows.map((r) => [
      pdfAsciiSafeText(r.customerName),
      ...st.stateLabels.map((_, i) => {
        const c = r.counts[i] ?? 0;
        return c > 0 ? c : '';
      }),
    ]);
    autoTable(doc, {
      ...pdfTablePlainTheme,
      startY: y,
      head,
      body,
      styles: { ...defaultTableStyles, fontSize: 7 },
      headStyles: { ...defaultHeadStyles, fontSize: 7 },
      didParseCell: (data) => {
        if (data.section === 'body' && typeof data.row.index === 'number') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
        }
      },
    });
    y = docNextY(doc, 8);
  }

  const wk = data.problemsCustomerWeekHeatmap;
  if (wk.weekLabels.length && wk.rows.length) {
    doc.addPage();
    y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Problems by customer & week', dateFrom, dateTo, { titleY: 12 });
    const head = [['Company', ...wk.weekLabels]];
    const body = wk.rows.map((r) => [
      pdfAsciiSafeText(r.customerName),
      ...wk.weekLabels.map((_, i) => {
        const c = r.counts[i] ?? 0;
        return c > 0 ? c : '';
      }),
    ]);
    const fs = wk.weekLabels.length > 14 ? 5.5 : 7;
    autoTable(doc, {
      ...pdfTablePlainTheme,
      startY: y,
      head,
      body,
      styles: { ...defaultTableStyles, fontSize: fs },
      headStyles: { ...defaultHeadStyles, fontSize: fs },
      didParseCell: (data) => {
        if (data.section === 'body' && typeof data.row.index === 'number') {
          data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
        }
      },
    });
    y = docNextY(doc, 8);
  }

  doc.addPage();
  y = drawKpiPdfTitleBlock(doc, 'KPI Dashboard - Problem details (preview)', dateFrom, dateTo, { titleY: 12 });
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y,
    head: [['Number', 'State', 'Opened', 'Group', 'Company', 'Priority', 'Statement', 'Resolution']],
    body: data.problemsDetailPreview.map((r) => [
      r.number,
      pdfAsciiSafeText(r.stateLabel),
      pdfAsciiSafeText(r.openedAt.slice(0, 19)),
      pdfAsciiSafeText(r.assignmentGroup ?? ''),
      pdfAsciiSafeText(r.company ?? ''),
      pdfAsciiSafeText(r.priorityLabel),
      truncatePdf(r.problemStatement, 72),
      pdfAsciiSafeText(r.resolutionLabel ?? ''),
    ]),
    styles: { ...defaultTableStyles, fontSize: 6 },
    headStyles: { ...defaultHeadStyles, fontSize: 6 },
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });

  finalizeKpiPdfFooters(doc);
  const fileName = kpiPdfFilename('kpi_dashboard_problems', dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}

export function exportKpiFilterContextPdf(
  viewTitle: string,
  dateFrom: string,
  dateTo: string,
  chips: Array<{ label: string; value: string }>
): KpiPdfExport {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const y0 = drawKpiPdfTitleBlock(doc, `KPI Dashboard - ${viewTitle}`, dateFrom, dateTo, {
    unitNote: 'Ticket rows: use CSV on this tab. This PDF lists active filters.',
  });
  const body =
    chips.length > 0
      ? chips.map((c) => [pdfAsciiSafeText(c.label), pdfAsciiSafeText(c.value)])
      : [['(no active filters beyond defaults)', '']];
  autoTable(doc, {
    ...pdfTablePlainTheme,
    startY: y0,
    head: [['Filter', 'Value']],
    body,
    styles: defaultTableStyles,
    headStyles: defaultHeadStyles,
    didParseCell: (data) => {
      if (data.section === 'body' && typeof data.row.index === 'number') {
        data.cell.styles.fillColor = data.row.index % 2 === 0 ? PDF_ZEBRA_A : PDF_ZEBRA_B;
      }
    },
  });
  finalizeKpiPdfFooters(doc);
  const slug = viewTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  const fileName = kpiPdfFilename(`kpi_${slug}`, dateFrom, dateTo);
  return { blob: doc.output('blob'), fileName };
}
